// it will return columns in the format required by the grid component
function getColumns(
  columnsData,
  columnConfig,
  columnStyle = {},
  preview = false,
  rowSpanOn,
  type,
  hideColumns,
  widgetType,
  isdownload,
  isPPTModalOpen,
  firstRowData
) {
  if (!Array.isArray(columnsData) || columnsData.length === 0) {
    return [];
  }
  // build columns and nested parent columns
  let { columns, nestedParentColumn } = buildColumnsAndGroups(
    columnsData,
    preview,
    type
  );
  // filter columns based on config
  if (
    nestedParentColumn?.length > 0 &&
    type === "grid" &&
    widgetType !== "predefined" &&
    columnConfig?.length > 0
  ) {
    let filterData = columnConfig.filter((cfg) => cfg.visible);
    let result = [];
    for (let i = 0; i < filterData.length; i++) {
      for (let j = 0; j < columns.length; j++) {
        if (filterData[i].title === columns[j]?.parent) {
          result.push(columns[j]);
        }
      }
    }
    columns = result;
  } else if (columnConfig?.length > 0 && type === "grid") {
    const columnMap = new Map(columns.map((col) => [col.field, col]));
    columns = columnConfig
      .filter((cfg) => cfg.visible)
      .map((cfg) => columnMap.get(cfg.title)) // reorder by config
      .filter(Boolean); // remove any that don't exist
  }

  // hide columns based on hideColumns prop
  if (Array.isArray(hideColumns) && hideColumns.length > 0 && type === "grid") {
    columns = columns.filter((col) => !hideColumns.includes(col.field));
  }

  // apply column style
  if (columnStyle && Object.keys(columnStyle).length > 0 && type === "grid") {
    columns = columns.map((col) => {
      const title = col?.field;
      if (title in columnStyle) {
        return {
          ...col,
          ...columnStyle[title],
          flex: null,
        };
      } else {
        return col;
      }
    });
  }

  // add row span only selected columns
  if (rowSpanOn) {
    columns = columns.map((col) => {
      if (col.title !== rowSpanOn) {
        return {
          ...col,
          rowSpanValueGetter: () => null,
        };
      } else if (col.title === rowSpanOn) {
        return {
          ...col,
        };
      }
    });
  }

  // align first column to left if data type is not int
  columns = columns.map((col, index) => {
    return {
      ...col,
      align:
        (index === 0 && col?.datatype === "varchar") ||
        (index === 0 && col?.datatype === "datetime")
          ? "left"
          : "center",
      headerAlign:
        (index === 0 && col?.datatype === "varchar") ||
        (index === 0 && col?.datatype === "datetime")
          ? "left"
          : "center",
    };
  });

  // prevent last column from resize
  columns = columns.map((col, ind) => {
    if (ind === columns.length - 1) {
      return {
        ...col,
        flex: 1,
        resizable: false,
      };
    } else {
      return {
        ...col,
        resizable: type == "grid" ? true : false,
      };
    }
  });

  // disable column resize for Ticket Status Summary Grid handle specific issue
  if (widgetType === "predefined") {
    columns = columns.map((col) => {
      if (col?.field?.length > 20) {
        return {
          ...col,
          minWidth: 400,
          resizable: false,
        };
      }
      return {
        ...col,
        flex: 1,
        resizable: false,
      };
    });
  }

  // remove specific columns for predefined widget
  columns = columns.filter((col) => {
    if (col?.title === "row_span") {
      return false;
    }
    return true;
  });

  // specific condition to show previous duration comparison first column data is left aligned
  const firstColField = firstRowData[columns[0]?.field];
  const dataType = typeof firstColField;
  columns = columns.map((col, index) => {
    if (index === 0) {
      return {
        ...col,
        align: dataType === "string" ? "left" : "center",
        headerAlign: dataType === "string" ? "left" : "center",
      };
    } else {
      return col;
    }
  });

  // remove all column style for preview and download mode
  if (isdownload || isPPTModalOpen) {
    columns = columns.map((col) => {
      return {
        ...col,
        flex: 1,
        minWidth: null,
        resizable: false,
        sortable: false,
      };
    });
  }

  return { columns, nestedParentColumn };
}

function buildColumnsAndGroups(columnsData, preview) {
  let flatColumns = [];

  function recurse(nodes) {
    return nodes.map((col, index) => {
      const alignValue = index === 0 ? "left" : "center";

      if (col?.children && col.children.length > 0) {
        // Create a group with nested children
        return {
          groupId: col?.title,
          headerName: col?.title,
          headerAlign: "center",
          children: recurse(col.children),
        };
      } else {
        // Leaf column
        const fieldName = col?.dataIndex || col?.title;
        flatColumns.push({
          field: fieldName,
          headerName: col?.title || fieldName,
          flex: 1,
          minWidth: 200,
          align: alignValue,
          headerAlign: alignValue,
          sortable: preview ? false : true,
          ...col,
        });

        // In grouping model, leaves are `{ field: "..." }`
        return { field: fieldName };
      }
    });
  }

  const columnGroupingModel = recurse(columnsData);
  const parentColumnsAvailable = columnGroupingModel.some(
    (col) => col?.groupId
  );
  if (parentColumnsAvailable) {
    function addParentGroupToColumns(columns, groups, parentName = null) {
      groups.forEach((item) => {
        if (item.children) {
          // If it’s a group, recurse into its children
          addParentGroupToColumns(columns, item.children, item.groupId);
        } else if (item.field) {
          // If it’s a field, find the column and assign the parent
          const col = columns.find((c) => c.field === item.field);
          if (col) {
            col.parent = parentName;
          }
        }
      });
      return columns;
    }

    addParentGroupToColumns(flatColumns, columnGroupingModel);
    flatColumns = flatColumns?.map((col) => {
      if (!col.parent) {
        return {
          ...col,
          parent: col?.title,
        };
      }
      return col;
    });
  }

  return {
    columns: flatColumns,
    nestedParentColumn: parentColumnsAvailable ? columnGroupingModel : [],
  };
}

// it will return data source in the format required by the grid component
function getDataSource(dataSource) {
  if (!Array.isArray(dataSource) || dataSource.length === 0) {
    return [];
  }
  let counter = 1;
  let result = dataSource.map((data) => {
    return {
      ...data,
      id: counter++,
      editable: false,
    };
  });
  return result;
}

export { getColumns, getDataSource };
